import { useMemo, useState } from "react";
import { Loader2, Users, Search } from "lucide-react";
import { toast } from "sonner";

import { useAdminUsers } from "@/hooks/queries/useAdminUsers";
import { useAdminOrganizations } from "@/hooks/queries/useAdminOrganizations";
import { useAcceptTrial } from "@/hooks/mutations/useAcceptTrial";
import {
  AdminUsersTable,
  daysInactive,
} from "@/components/admin/AdminUsersTable";
import { ManageUserDialog } from "@/components/admin/ManageUserDialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { AdminUser } from "@/types/domain";

type StatusFilter = "all" | "pending" | "active" | "expired" | "norequest";
type SortBy = "createdAt" | "lastSeen";

const SORT_LABELS: Record<SortBy, string> = {
  createdAt: "Más recientes",
  lastSeen: "Más tiempo sin entrar",
};

const STATUS_LABELS: Record<StatusFilter, string> = {
  all: "Todos",
  pending: "Pendientes de revisión",
  active: "Con acceso vigente",
  expired: "Vencidos",
  norequest: "Sin solicitud",
};

function matchesStatus(user: AdminUser, status: StatusFilter): boolean {
  switch (status) {
    case "pending":
      return user.betaformcompleted && !user.betaaccepted;
    case "active":
      return !!user.subscription?.isActive;
    case "expired":
      return !!user.subscription && !user.subscription.isActive;
    case "norequest":
      return !user.betaformcompleted;
    default:
      return true;
  }
}

export default function AdminUsers() {
  const { data = [], isLoading } = useAdminUsers();
  const { data: organizations = [] } = useAdminOrganizations();
  const acceptTrial = useAcceptTrial();

  // La empresa y las líneas se cruzan acá en vez de repetir la consulta en el
  // endpoint de usuarios: son los mismos datos que alimentan la vista Empresas.
  const organizationsById = useMemo(
    () => new Map(organizations.map((org) => [org.id, org])),
    [organizations],
  );

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [sortBy, setSortBy] = useState<SortBy>("createdAt");
  const [managedUser, setManagedUser] = useState<AdminUser | null>(null);

  const pendingCount = data.filter(
    (u) => u.betaformcompleted && !u.betaaccepted,
  ).length;

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = data.filter(
      (user) =>
        matchesStatus(user, status) &&
        (!term ||
          user.name.toLowerCase().includes(term) ||
          user.email.toLowerCase().includes(term)),
    );

    if (sortBy === "createdAt") return rows;

    // Los abandonados primero; "sin rastro" es el caso más extremo, va arriba.
    return [...rows].sort(
      (a, b) =>
        (daysInactive(b) ?? Number.MAX_SAFE_INTEGER) -
        (daysInactive(a) ?? Number.MAX_SAFE_INTEGER),
    );
  }, [data, search, status, sortBy]);

  function onAccept(user: AdminUser) {
    acceptTrial.mutate([user.id], {
      onSuccess: (res) =>
        res?.accepted > 0
          ? toast.success(`${user.name} fue aceptado y tiene su trial activo.`)
          : toast.warning(`${user.name} ya tenía su acceso otorgado.`),
      onError: () =>
        toast.error("No pudimos aceptar al usuario. Intenta de nuevo."),
    });
  }

  return (
    <div className="mx-auto w-full max-w-[1600px] flex-1 px-4 py-6 md:px-6">
      <div className="mb-5">
        <h1 className="text-2xl font-bold tracking-tight">Usuarios</h1>
        <p className="text-sm text-muted-foreground">
          Quién usa la plataforma, con qué plan y qué está buscando. Abre una fila
          para ver sus términos.
          {pendingCount > 0 && (
            <span className="ml-1 font-medium text-amber-600">
              {pendingCount} pendiente{pendingCount === 1 ? "" : "s"} de
              revisión.
            </span>
          )}
        </p>
      </div>

      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscar por nombre o correo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select
          value={status}
          onValueChange={(value) => setStatus(value as StatusFilter)}
        >
          <SelectTrigger className="sm:w-[220px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={sortBy}
          onValueChange={(value) => setSortBy(value as SortBy)}
        >
          <SelectTrigger className="sm:w-[220px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(SORT_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-2xl border bg-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center">
            <Users className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {data.length === 0
                ? "No hay usuarios."
                : "Ningún usuario coincide con el filtro."}
            </p>
          </div>
        ) : (
          <AdminUsersTable
            users={filtered}
            organizationsById={organizationsById}
            acceptingUserIds={
              acceptTrial.isPending ? (acceptTrial.variables ?? []) : []
            }
            onAccept={onAccept}
            onManage={setManagedUser}
          />
        )}
      </div>

      <ManageUserDialog
        user={managedUser}
        onOpenChange={(open) => !open && setManagedUser(null)}
      />
    </div>
  );
}
